// src/services/aeo/clientDocsTypes.ts
//
// The documents contract for phase 1 (Oura reads the Google Docs), exactly as
// map_contract.md A.1 and A.3 write it, plus the result unions the service
// hands to screens. Re-exported from clientDocs.ts: import from there.
//
// Rule of the contract: every key is present, and null means unknown or not
// measured. A screen reads null as "Not read yet" or "Not measured", never as
// a zero or a green tick.
//
// The B26 addendum (2 Oct 2026, written for phase 2 before B4, B5 and B10 are
// built) adds, all optional until the server sends them: posts Oura writes on
// the client's behalf (`DocAuthor.onBehalfOf`, `DocPost.via`), question ids
// and kinds on threads, each code's state from its own questions, and the
// amber flag `resolved_without_agreement`. The write contract itself lives in
// clientDocsWriteTypes.ts.

import type {
    AnswerVia,
    ClientAnswerKind,
    DocQuestion,
    DocsWriteErrorCode,
    DocsWriteStatus,
    MerchantAnswered,
    QuestionCheck,
    QuestionKind,
    SkillRun,
} from "./clientDocsWriteTypes";
import { DOCS_WRITE_ERROR_CODES } from "./clientDocsWriteTypes";

/* ------------------------------------------------------------------------- *
 * A.1 Enums
 * ------------------------------------------------------------------------- */

export type DocSide = "us" | "client";
/** Who posted: oura@, no-reply@, a staffer's own ourteam account, anyone else. */
export type DocAccount = "oura" | "no_reply" | "staff" | "client";
export type ThreadKind = "question" | "our_note" | "placeholder" | "raised_by_client";
export type ThreadState = "waiting_on_client" | "needs_us" | "approved" | "late";
/** `done`: staff marked it finished without the client (backend #6364, #4541). */
export type DocState = ThreadState | "nothing_asked" | "not_shared" | "done";
export type CodeState = "not_sent_yet" | "not_due" | "not_shared" | "waiting_on_client" | "needs_us" | "late" | "approved" | "nothing_asked" | "done";
export type FileType = "gdoc" | "word" | "pdf" | "sheet" | "other";
export type CodeSource = "name" | "title" | "staff";
export type LinkRole = "reader" | "commenter" | "writer";
export type DocFlagCode =
    | "link_sharing" | "edit_access" | "internal_notes" // red
    | "no_code" | "name_title_disagree" | "code_not_in_contract" // amber
    | "word_file" | "comments_unreadable" // amber
    | "resolved_without_agreement"; // amber, B26
export type RequestStatus = "open" | "answered" | "done";
export type MerchantDocState = "needs_your_reply" | "with_us" | "approved" | "nothing_asked";

/* ------------------------------------------------------------------------- *
 * A.3 Shapes
 * ------------------------------------------------------------------------- */

/** Route 1, `GET /documents/connection`: the one Drive connection, as oura@. */
export interface DocsConnection {
    status: "connected" | "action_needed" | "not_connected";
    /** "oura@ourteam.ai" from OURA_DRIVE_SUBJECT; null = not configured. */
    actingAs: string | null;
    /** Phase 1: Drive read-only. */
    access: "read" | null;
    /** Last time a token was minted as oura@. */
    checkedAt: string | null;
    /** Last finished 5-minute run, any client. */
    lastSyncAt: string | null;
    lastSyncError: string | null;
    /** 5 */
    syncEveryMinutes: number;
    /** The server's sentence, e.g. "Reads client folders as oura@ourteam.ai. Last read 4 min ago." */
    detail: string;
    /** B11, additive: write access as oura@. Absent = the server does not send it yet ("Read only"). */
    write?: DocsWriteStatus | null;
}

/** Routes 3 to 6: a client's Drive folder. Route 3 answers null when none is linked. */
export interface DocsFolder {
    businessId: string;
    folderId: string;
    url: string;
    /** The Drive folder name; null until the first read. */
    name: string | null;
    linkedAt: string;
    linkedBy: string;
    /** null = not read yet. */
    lastReadAt: string | null;
    lastError: { code: string; message: string; at: string } | null;
    /** null = not read yet. */
    fileCount: number | null;
}

/** `POST /businesses/{id}/docs-folder/create` (ourteam-backend#6178): the folder it linked, and whether it made it. */
export interface DocsFolderCreated extends DocsFolder {
    /** false: the folder was already in Drive, so nothing was made. */
    created: boolean;
}

/** Route 7, `GET /businesses/{id}/documents`: the board. */
export interface BusinessDocuments {
    folder: DocsFolder | null;
    connection: DocsConnection["status"];
    readAt: string | null;
    /** Every file in the folder and its subfolders; trashed files dropped. */
    documents: StaffDocument[];
    /** 1 row per code (and outlet): the board's groups. */
    codes: CodeRow[];
    /** null = folder never read. */
    counts: {
        waitingOnClient: number;
        needsUs: number;
        late: number;
        notSentYet: number;
        approved: number;
        noCode: number;
        redFlags: number;
    } | null;
    /**
     * The signed plan's start (YYYY-MM-DD) and term (ourteam-backend#6868), for a
     * monthly doc's "Month N" by `termMonth`, the rule the server names it with.
     * null without a signed plan; absent from a server before it.
     */
    plan?: { startedAt: string; termMonths: number } | null;
}

export interface CodeRow {
    /** "S2" */
    code: string;
    /** "MK" */
    outlet: string | null;
    /** The engagement deliverable's label. */
    label: string | null;
    inContract: boolean;
    state: CodeState;
    documentIds: string[];
    /**
     * YYYY-MM-DD: the last working day of the plan month window the code's
     * doc is due in (B5: the engagement's month for the code, the window from
     * Oura's timeline). null = the code goes through no client doc, or the
     * plan has no start date.
     */
    expectedBy: string | null;
    /**
     * Added for the board (handoff B10, additive; optional until route 7
     * sends it): the plan month whose window ends on `expectedBy`. 1 is the
     * Foundation build, so the screen can say "the end of the Foundation
     * build". null when `expectedBy` is null.
     */
    expectedMonth?: number | null;
    /** S2: engagement.outlets; G1: 1 per plan month started; otherwise 1; null = no doc expected. */
    expectedDocs: number | null;
    /** Shared docs covering the row; for G1, those that count toward it. */
    sharedDocs: number;
    /** A not_sent_yet row: past `expectedBy`. Otherwise: any shared doc is late. */
    late: boolean | null;
}

export interface DocSharedWith {
    email: string | null;
    name: string | null;
    role: "reader" | "commenter" | "writer" | "owner";
    ours: boolean;
}

export interface StaffDocument {
    id: string;
    driveFileId: string;
    /** A doc type staff named themselves ("Treatment Menu", ourteam-backend#6516). Absent from a backend before it. */
    custom?: boolean;
    /** The Drive file name as it stands. */
    name: string;
    /** Line 1 of the doc; null = not a Google Doc, or not read yet. */
    pageTitle: string | null;
    fileType: FileType;
    /** webViewLink: "Open in Google Docs". */
    url: string;
    /** "Answer Plan", read from the name. */
    docType: string | null;
    /** ["S1","S3","B1","G4"]; [] = no code. */
    codes: string[];
    /** "MK" from "S2-MK". */
    outlet: string | null;
    codeSource: CodeSource | null;
    /** "1.0" */
    version: string | null;
    /** YYYY-MM-DD, from the name. */
    docDate: string | null;
    /** Staff marked it internal through Pick a code. */
    notClientDoc: boolean;
    owner: string | null;
    modifiedAt: string | null;
    sharing: {
        clientCanSee: boolean;
        sharedWith: DocSharedWith[];
        /** null = link sharing off. */
        linkSharing: LinkRole | null;
        /** When Oura first saw it shared with the client; null = not shared, or unknown. */
        sharedAt: string | null;
    };
    /** YYYY-MM-DD */
    replyBy: string | null;
    replyBySource: "default" | "staff" | null;
    /** null = comments not read yet. */
    state: DocState | null;
    /**
     * Staff marked it done, without sharing it (backend #6364): when, and who.
     * Null: not done. Absent: a backend that cannot mark one, so no control shows (#4541).
     */
    done?: { at: string; by: string } | null;
    late: boolean | null;
    lateWorkingDays: number | null;
    waitingOn: "client" | "us" | null;
    waitingSince: string | null;
    waitingWorkingDays: number | null;
    /** `late` is a subset of `waitingOnClient`. */
    questions: { out: number; back: number; waitingOnClient: number; needsUs: number; approved: number; late: number } | null;
    raisedByClient: { total: number; needsUs: number } | null;
    notCounted: { ourNotes: number; placeholders: number; internalNotes: number } | null;
    lastPost: DocPostSummary | null;
    /** "The client's last words". */
    lastClientPost: DocPostSummary | null;
    firstClientReplyAt: string | null;
    /** Working days from share to the first answer. */
    tookWorkingDays: number | null;
    flags: DocFlag[];
    /** Last time Oura read its comments. */
    readAt: string | null;
    /**
     * B26, additive: resolved questions without the client's agreement (the
     * amber flag's count). null = comments not read; absent = not sent yet.
     */
    unagreedResolves?: number | null;
    /**
     * Oura's fill of a doc made from a template (ourteam-backend#5517). null =
     * no fill was ever started for it; absent = the server does not send it yet.
     * `canRerun` is the server's own answer to "will POST /documents/{id}/fill be
     * taken", so the screen never works it out from `status` or an age.
     */
    fill?: DocFill | null;
    /** The Word file this doc was converted from (ourteam-backend#6038). null = none; absent = not sent yet. */
    convertedFrom?: DocRef | null;
    /** On a Word file: the Google Doc it was converted to, the newest live one (#6038). */
    convertedTo?: DocRef | null;
    /**
     * The newest skill run that wrote into this doc (ourteam-backend#6161), or
     * null when no skill did; absent from a server that does not send it yet.
     * A run still `running` past the runner's 30 minutes is served as `failed`.
     */
    skillRun?: SkillRun | null;
}

/** Another doc, by id and its name as it stands. */
export interface DocRef {
    id: string;
    name: string;
}

/** `StaffDocument.fill`, and `fill` on what Create and Re-run answer (#4185). */
export interface DocFill {
    /** `running` also covers a queued fill; a fill that never finished reads `failed`. */
    status: "running" | "done" | "failed";
    /** The finer word: queued, running, done, failed or stalled. */
    state: string;
    startedAt: string | null;
    finishedAt: string | null;
    /** The server's own sentence for why it ended without filling the doc. */
    error: string | null;
    attempts: number;
    canRerun: boolean;
    /**
     * A filled doc can be filled again in place (ourteam-backend#6803: no versions,
     * so no second doc): a fresh template copy for the same doc, the last fill kept
     * beside it. Absent from an older backend, which refused it.
     */
    canRefill?: boolean;
}

export interface DocFlag {
    code: DocFlagCode;
    tone: "red" | "amber";
    message: string;
    detail: string[] | null;
}

/** Route 8, `GET /documents/{documentId}`. */
export interface DocumentDetail extends StaffDocument {
    threads: DocThread[];
    /**
     * B15, additive: the questions Oura keeps for this doc, drafts first, then
     * sent ones by number. Absent = the server does not send it yet.
     */
    questionList?: DocQuestion[];
    /** Lane 3, additive: the 3 counts that must agree after Send to the doc. null = no question sent from Oura; absent = not sent yet. */
    questionCheck?: QuestionCheck | null;
    /**
     * A Word file's own comments, read from the .docx itself (they are not
     * Google Drive comments, so `threads` is empty). Read-only: converting to a
     * Google Doc brings them in as threads. Absent or null: not read (#4565).
     */
    wordFile?: { chars: number; comments: WordComment[] } | null;
}

/** One comment inside a Word file: who, when, what, and the text it is on. */
export interface WordComment {
    id: string;
    author: string;
    /** ISO time, or null when the file does not say. */
    at: string | null;
    text: string;
    /** The document text the comment is anchored to. */
    quoted: string | null;
}
export interface DocAuthor {
    /** For a post on the client's behalf, the client's name (the same as `onBehalfOf`). */
    name: string;
    side: DocSide;
    account: DocAccount;
    /**
     * B26: set when oura@ wrote the post for the client (an answer given in
     * Oura, or one staff logged from an email, WhatsApp or a call). The post
     * then counts as the client's: `side` is "client", `account` "oura".
     */
    onBehalfOf?: string | null;
}
export interface DocPost {
    id: string;
    author: DocAuthor;
    text: string;
    at: string;
    action: "reply" | "resolve" | "reopen";
    /** Where it was written: in the doc, or from Oura (phase 2). */
    where: "doc" | "oura";
    /** B26: how a post on the client's behalf arrived; null for every other post. */
    via?: AnswerVia | null;
    /** B26: the client's answer kind, for a post on their behalf from Oura's answer screen; null otherwise. */
    answer?: ClientAnswerKind | null;
    /** B22: the staff member who logged an email, WhatsApp or call answer. */
    loggedBy?: string | null;
    /** B22: when the client said it, as logged; null = when posted. */
    saidAt?: string | null;
    /** B20: the staff member a reply from Oura is signed with. */
    signedBy?: string | null;
    /** B21: when Oura last edited this post of its own. */
    editedAt?: string | null;
    /** B19: a file the client sent with the answer, stored in their Drive folder. */
    file?: { name: string; url: string } | null;
}
export interface DocPostSummary extends DocPost {
    threadId: string;
}
export interface DocThread {
    id: string;
    driveCommentId: string;
    kind: ThreadKind;
    /** A "[S2-MK]" prefix when present; otherwise null and the doc's codes apply. */
    code: string | null;
    /** null = an unanswered note or placeholder, or a client-raised thread we replied to. */
    state: ThreadState | null;
    /** An our_note opened from a staffer's own account. */
    internal: boolean;
    /** quotedFileContent.value */
    quoted: string | null;
    openedBy: DocAuthor;
    openedAt: string;
    text: string;
    /** The replies, oldest first, resolves and reopens included. The opening comment is `text`. */
    posts: DocPost[];
    resolved: boolean;
    resolvedBy: DocAuthor | null;
    resolvedAt: string | null;
    lateWorkingDays: number | null;
    /** B26: the question Oura keeps for this thread (its comment id is on record); null = a thread Oura did not open. */
    questionId?: string | null;
    /** B26: the question's kind, from Oura's record; null without one. */
    questionKind?: QuestionKind | null;
    /** B26: its number in the doc's list; null without one. */
    number?: number | null;
    /** B26: a choice question's options; [] otherwise. */
    options?: string[];
    /**
     * B26: for a resolved question, whether the resolve carries the client's
     * agreement: they resolved it themselves (or Agreed in Oura), or their
     * last word before our resolve was an agreement (for a choice, text or
     * file question, any answer but a Change). false is the amber flag.
     * null = not resolved, or not a question.
     */
    resolvedAgreed?: boolean | null;
}

/** Routes 9 and 10. */
export type PickCodeIn = { codes: string[]; outlet?: string | null } | { notClientDoc: true };

/** Route 11, `GET /documents/summary`: All clients. */
export interface DocsAcrossClients {
    syncedAt: string | null;
    /**
     * One entry per client business. The four counts are only meaningful when
     * `folderLinked` is true and the folder has been read: the contract types
     * them as numbers, so an unlinked folder answers 0 there.
     */
    clients: AcrossClient[];
    /** Oldest waitingSince first. */
    waitingOnClient: AcrossRow[];
    /** Oldest waitingSince first. */
    needsUs: AcrossRow[];
    /** Most lateWorkingDays first. */
    late: AcrossRow[];
}
export interface AcrossClient {
    businessId: string;
    name: string;
    folderLinked: boolean;
    /** null = no answered doc yet. */
    typicalReplyWorkingDays: number | null;
    replyBasis: { answered: number; sent: number };
    waitingOnClient: number;
    needsUs: number;
    late: number;
    notSentYet: number;
    /**
     * Added for All clients (handoff B10, contract point 10; optional until
     * route 11 sends them). `readAt`: the folder's last good read, null = not
     * linked or never read, so the counts above are unknown. `folderError`:
     * the folder's `lastError`, null = reading fine. `redFlags`: docs
     * carrying a red sharing flag, the board's `counts.redFlags`; null while
     * `readAt` is null.
     */
    readAt?: string | null;
    folderError?: { code: string; message: string } | null;
    redFlags?: number | null;
    /**
     * Added for All clients (handoff B10, additive): the earliest `expectedBy`
     * (YYYY-MM-DD) among this client's code rows in `not_sent_yet`, so the
     * most overdue client sorts first. null = none owed, or the folder is not
     * read; absent = the server does not send it yet.
     */
    notSentOldestBy?: string | null;
    /**
     * Added for All clients (handoff B10, additive): this client's route 7
     * code rows in `not_sent_yet`, each with its due date and whether it is
     * past it, so All clients counts a code past due as late (YW, 29 Sep
     * 2026) and names the doc that clears it. null = the folder is not read;
     * absent = the server does not send it yet.
     */
    notSentCodes?: NotSentCode[] | null;
}
/** A code the plan expects a doc for by now, with none shared: a route 7 `not_sent_yet` row, trimmed. */
export interface NotSentCode {
    code: string;
    outlet: string | null;
    /** The engagement deliverable's label. */
    label: string | null;
    /** YYYY-MM-DD, the row's `expectedBy`. */
    expectedBy: string | null;
    expectedMonth: number | null;
    /** Past `expectedBy`: "Was due". */
    late: boolean | null;
}
export interface AcrossRow {
    businessId: string;
    businessName: string;
    documentId: string;
    title: string;
    codes: string[];
    outlet: string | null;
    url: string;
    sharedAt: string | null;
    replyBy: string | null;
    waitingSince: string | null;
    waitingWorkingDays: number | null;
    lateWorkingDays: number | null;
    questions: { out: number; back: number } | null;
    lastClientPost: DocPostSummary | null;
    /**
     * Added for All clients (handoff B10, additive): the codes of this doc's
     * red sharing flags only, so the row names what is exposed. Absent = the
     * server does not send it yet.
     */
    redFlags?: DocFlagCode[];
}

/**
 * Route 12: `ClientPagePayload.documents`. Absent = Oura doesn't read docs
 * here yet; [] = nothing shared with them. Only docs the client can see, only
 * question threads, never flags, sharing lists or internal notes.
 */
export interface MerchantDocument {
    id: string;
    /** Page title, else docType + codes, else the file name. */
    title: string;
    /** A doc type the client named itself (ourteam-backend#6520): its title is shown as it is. */
    custom?: boolean;
    /**
     * The doc's name from its FILE name, in the client's words ("Google profile, Luxe Dental",
     * "Answer Plan"), never its line 1 (ourteam-backend#6582). Only the Shared docs list uses it.
     * Absent from a backend before it, or null when the file names nothing.
     */
    fileTitle?: string | null;
    codes: string[];
    outlet: string | null;
    fileType: FileType;
    /** "Open the document". */
    url: string;
    sharedAt: string | null;
    replyBy: string | null;
    late: boolean | null;
    lateWorkingDays: number | null;
    /** waiting_on_client|late -> needs_your_reply; needs_us -> with_us. */
    state: MerchantDocState;
    /** `waiting` counts the questions still waiting on the client. */
    questions: { out: number; waiting: number; approved: number } | null;
    /**
     * Question threads still waiting on the client, in doc order. B26 adds the
     * question's record, so the answer screen can offer the right answer
     * (absent = the server does not send it yet).
     */
    openQuestions: {
        id: string;
        text: string;
        askedAt: string;
        code: string | null;
        questionId?: string | null;
        kind?: QuestionKind | null;
        number?: number | null;
        options?: string[];
    }[];
    updatedAt: string | null;
    /**
     * The client has not opened it yet (ourteam-backend#6108). Absent on a
     * server before that ticket, which reads as "not known", never as unread:
     * a dot on every doc they had already read would be worse than none.
     */
    unread?: boolean;
    /** When the client first opened it, or null while they have not (ourteam-backend#6108). */
    readAt?: string | null;
    /**
     * Lane 5, additive (B19): the questions they have answered that wait on
     * us or are approved, newest first, so they see what they said and
     * when. Absent = the server does not send it yet.
     */
    answered?: MerchantAnswered[];
}

/* ------------------------------------------------------------------------- *
 * 13 to 17: Ask for a change (the full type lives in clientReview.ts, which
 * owns ChangeRequest; these are the request bodies).
 * ------------------------------------------------------------------------- */

export interface ChangeRequestIn {
    note: string;
    requestedBy?: string;
    fileKeys?: string[];
}
export interface ChangeRequestPatchIn {
    status?: RequestStatus;
    smallChange?: boolean;
    confirmOverLimit?: boolean;
}

/* ------------------------------------------------------------------------- *
 * Results. Reads never throw to a screen: they answer one of these, so a
 * screen can tell loading, empty, error and unknown apart.
 * ------------------------------------------------------------------------- */

/**
 * The server's own refusal codes (map_contract A.3, "Errors", plus B2's
 * `drive_unavailable`, 502 when Google does not answer a link), and the
 * write routes' refusals (clientDocsWriteTypes.ts).
 */
export type DocsErrorCode =
    | "folder_url_invalid"
    | "folder_not_readable"
    | "folder_linked_elsewhere"
    | "docs_not_configured"
    | "drive_unavailable"
    | "unknown_code"
    | "outlet_invalid"
    | "codes_in_name"
    | "reply_by_in_past"
    | "note_required"
    | "note_too_long"
    | "file_not_yours"
    | "upload_empty"
    | "upload_too_large"
    | "upload_type_not_allowed"
    | "reply_required"
    | "small_change_already_filed"
    // Delete a file from the folder (backend #6451).
    | "confirm_name_mismatch"
    | "not_file_owner"
    | "file_not_found_in_drive"
    // A custom doc type (ourteam-backend#6516, #6517); an outlet the plan does not have.
    | "custom_type_invalid"
    | "code_not_in_plan"
    | "kind_not_for_custom"
    | "kind_not_for_type"
    | "outlet_unknown"
    // The date in a new doc's name is after today (ourteam-backend#6556).
    | "name_date_in_future"
    // Make the client's Drive folder (ourteam-backend#6178).
    | "already_linked"
    | "ambiguous_folder"
    | "projects_folder_missing"
    | "docs_folder_busy"
    // The day the work was done (`doneOn`) is after today (ourteam-backend#6414).
    | "done_on_future"
    | DocsWriteErrorCode;

export const DOCS_ERROR_CODES: readonly DocsErrorCode[] = [
    "folder_url_invalid",
    "folder_not_readable",
    "folder_linked_elsewhere",
    "docs_not_configured",
    "drive_unavailable",
    "unknown_code",
    "outlet_invalid",
    "codes_in_name",
    "reply_by_in_past",
    "note_required",
    "note_too_long",
    "file_not_yours",
    "upload_empty",
    "upload_too_large",
    "upload_type_not_allowed",
    "reply_required",
    "small_change_already_filed",
    "confirm_name_mismatch",
    "not_file_owner",
    "file_not_found_in_drive",
    "custom_type_invalid",
    "code_not_in_plan",
    "kind_not_for_custom",
    "kind_not_for_type",
    "outlet_unknown",
    "name_date_in_future",
    "already_linked",
    "ambiguous_folder",
    "projects_folder_missing",
    "docs_folder_busy",
    "done_on_future",
    ...DOCS_WRITE_ERROR_CODES,
];

/**
 * Why a call failed, not just that it did.
 * - `network`: no answer reached us (or the sample store's error mode).
 * - `unauthorised` / `forbidden` / `notFound`: 401 / 403 / 404 from a route that exists.
 * - `notServed`: the route is not deployed yet. Staff see "not available yet", never zeros.
 * - `rejected`: the server refused with one of its own codes; read `code`.
 */
export type DocsFailReason = "network" | "unauthorised" | "forbidden" | "notFound" | "notServed" | "rejected";

export interface DocsFailure {
    ok: false;
    reason: DocsFailReason;
    /** The server's code when `reason` is "rejected"; null otherwise. */
    code: DocsErrorCode | null;
    /** HTTP status, or null when no answer came back. */
    status: number | null;
    /** A plain sentence, safe to show as it is. */
    message: string;
    /** unknown_code: the code names the server did not know. */
    badCodes: string[] | null;
    /** folder_linked_elsewhere: the business the folder already belongs to. */
    linkedTo: { businessId: string; name: string | null } | null;
    /** small_change_already_filed: this month's small change. */
    existingId: string | null;
    /** ambiguous_folder: the folders that share the name, to open and link by hand. */
    folders: { id: string; url: string }[] | null;
}

export type DocsResult<T> = { ok: true; data: T } | DocsFailure;

/** Read and write results the service returns. */
export type DocsConnectionResult = DocsResult<DocsConnection>;
export type DocsFolderResult = DocsResult<DocsFolder | null>;
export type BusinessDocumentsResult = DocsResult<BusinessDocuments>;
export type DocumentDetailResult = DocsResult<DocumentDetail>;
export type StaffDocumentResult = DocsResult<StaffDocument>;
export type DocsAcrossClientsResult = DocsResult<DocsAcrossClients>;
